import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, finalize } from 'rxjs';
import { AgentRefreshService } from '../../agent/agent-refresh.service';
import { OrganisationFeatures } from '../../services/organisation.service';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';
import {
  BYTES_PER_MB,
  CONTRACT_LIMIT_KEYS,
  CONTRACT_LIMITS_WITHOUT_UNLIMITED,
  ClientContract,
  ContractAdminPayload,
  ContractAuditEntry,
  ContractBillingCycle,
  ContractInvoiceMode,
  ContractLimitKey,
  ContractLimits,
  ContractRenewal,
  ContractService,
  ContractTemplate,
  ContractTemplateBody,
  ContractTemplatePlan,
  ContractTemplateTerms,
  ContractUpsertBody,
  toIsoDate,
} from '../../services/contract.service';

const CYCLE_MONTHS: Record<ContractBillingCycle, number> = {
  month: 1,
  quarter: 3,
  year: 12,
};

/** Startwerte im Feld „Ausgangswerte übernehmen“: der heutige Stand des
 *  Kunden, leer, eine gespeicherte Vertragsvorlage oder ein Standardplan. */
const SOURCE_CURRENT = 'current';
const SOURCE_EMPTY = 'empty';
const SOURCE_TEMPLATE = 'tpl:';
const SOURCE_PLAN = 'plan:';

/** Laufzeit in Monaten aus Beginn und letztem Tag (gerundet), und zurueck. */
function termMonths(startsOn: string, endsOn: string): number | null {
  if (!startsOn || !endsOn) return null;
  const days = (Date.parse(endsOn) - Date.parse(startsOn)) / 86400000 + 1;
  const months = Math.round(days / 30.4375);
  return months >= 1 ? months : null;
}

function lastDayAfter(startsOn: string, months: number): string {
  const [y, m, d] = startsOn.split('-').map(Number);
  const target = new Date(y, m - 1 + months, 1);
  const lastOfMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d, lastOfMonth));
  target.setDate(target.getDate() - 1);
  return toIsoDate(target);
}

/** Eine Zeile im Aenderungsverlauf, fertig fuer das Template. */
interface AuditView {
  id: string;
  action: string;
  who: string;
  at?: string | null;
  note?: string | null;
  rows: { field: string; label: string; before: string; after: string }[];
}

/**
 * Super-Admin: individuellen Vertrag eines Kunden aktivieren, aendern oder
 * beenden (PRD features/prd/custom-contracts.md). Sitzt im Reiter „Vertrag“
 * des Organisation-Dialogs; dessen Footer ruft `submit()` / `endContract()`,
 * damit die Primaraktion wie ueberall rechts im Footer steht (CLAUDE.md
 * Regel 16).
 */
@Component({
  selector: 'app-contract-editor',
  templateUrl: './contract-editor.component.html',
  styleUrl: './contract-editor.component.scss',
})
export class ContractEditorComponent implements OnInit, OnDestroy {
  @Input() clientId!: string;
  @Input() companyName?: string | null;
  /** Nach jedem erfolgreichen Aktivieren, Speichern oder Beenden. */
  @Output() changed = new EventEmitter<ContractAdminPayload>();

  readonly limitKeys = CONTRACT_LIMIT_KEYS;
  readonly cycles: ContractBillingCycle[] = ['month', 'quarter', 'year'];
  readonly renewals: ContractRenewal[] = ['auto', 'none'];
  readonly invoiceModes: ContractInvoiceMode[] = ['approve', 'auto_send'];
  readonly sourceCurrent = SOURCE_CURRENT;
  readonly sourceEmpty = SOURCE_EMPTY;
  /** Funktionen, die ein Vertrag schalten kann — dazu jede, die der Kunde
   *  oder der Plan schon traegt (auch wenn das Portal sie nicht kennt). */
  featureKeys: string[] = Object.values(OrganisationFeatures);

  payload?: ContractAdminPayload;
  loading = false;
  loadError = false;
  saving = false;
  ending = false;
  templateBusy = false;

  form: FormGroup;
  /** Spalte „Ausgangswert“: die gewaehlten Startwerte, bei einem laufenden
   *  Vertrag der Plan, aus dem er entstand. */
  referenceLimits: ContractLimits = {};
  auditView: AuditView[] = [];

  /** Stand nach dem Laden — Grundlage fuer „nur Geaendertes senden“. */
  private initial?: ContractUpsertBody;
  private subs: Subscription[] = [];

  constructor(
    protected fb: FormBuilder,
    protected contractService: ContractService,
    protected dialogService: NbDialogService,
    protected toastr: NbToastrService,
    protected t: TranslateService,
    private agentRefresh: AgentRefreshService
  ) {
    const limitGroups: Record<string, FormGroup> = {};
    for (const key of CONTRACT_LIMIT_KEYS) {
      limitGroups[key] = this.fb.group({
        value: [null as number | null, [Validators.required, Validators.min(0)]],
        unlimited: [false],
      });
    }
    this.form = this.fb.group({
      source: [SOURCE_CURRENT as string],
      features: this.fb.group({}),
      display_name: ['', [Validators.required, Validators.maxLength(255)]],
      starts_on: ['', Validators.required],
      ends_on: [''],
      renewal: ['auto' as ContractRenewal, Validators.required],
      notice_days: [null as number | null, [Validators.min(0), Validators.max(730)]],
      billing_cycle: ['month' as ContractBillingCycle, Validators.required],
      base_fee_eur: [null as number | null, [Validators.required, Validators.min(0)]],
      payment_terms_days: [14, [Validators.required, Validators.min(0), Validators.max(180)]],
      invoice_email: ['', [Validators.email, Validators.maxLength(255)]],
      buyer_reference: ['', Validators.maxLength(50)], // Leitweg-ID, alphacount limit
      po_number: ['', Validators.maxLength(64)],
      invoice_mode: ['approve' as ContractInvoiceMode, Validators.required],
      billing_starts_on: [''],
      note: ['', Validators.maxLength(2000)],
      usage: this.fb.group({
        enabled: [false],
        included_per_period: [0, [Validators.min(0)]],
        unit_size: [1000, [Validators.min(1)]],
        unit_price_eur: [null as number | null, [Validators.min(0)]],
      }),
      limits: this.fb.group(limitGroups),
    });

    // „unbegrenzt“ schaltet das Zahlenfeld ab — ein abgeschaltetes Feld ist
    // kein Pflichtfeld mehr (Regel 5: Pflicht kommt aus den Validatoren).
    for (const key of CONTRACT_LIMIT_KEYS) {
      const group = this.limitGroup(key);
      this.subs.push(
        group.get('unlimited')!.valueChanges.subscribe((on: boolean) => {
          const value = group.get('value')!;
          if (on) {
            value.disable({ emitEvent: false });
          } else {
            value.enable({ emitEvent: false });
          }
        })
      );
    }
    const usage = this.form.get('usage') as FormGroup;
    this.subs.push(
      usage.get('enabled')!.valueChanges.subscribe((on: boolean) => {
        const price = usage.get('unit_price_eur')!;
        price.setValidators(on ? [Validators.required, Validators.min(0)] : [Validators.min(0)]);
        price.updateValueAndValidity({ emitEvent: false });
      })
    );
  }

  ngOnInit(): void {
    this.load();
    // The portal assistant changed this contract or one of its invoices.
    this.subs.push(
      this.agentRefresh.on('contracts').subscribe(() => this.load()),
      this.agentRefresh.on('invoices').subscribe(() => this.load())
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  // ------------------------------------------------------------------ state

  get contract(): ClientContract | null {
    return this.payload?.contract ?? null;
  }

  get isActive(): boolean {
    return !!this.contract;
  }

  /** Fuer den Footer des Dialogs: erst wenn geladen, laesst sich speichern. */
  get ready(): boolean {
    return !!this.payload && !this.loading;
  }

  get busy(): boolean {
    return this.saving || this.ending;
  }

  get templates(): ContractTemplatePlan[] {
    return this.payload?.templates ?? [];
  }

  get contractTemplates(): ContractTemplate[] {
    return this.payload?.contract_templates ?? [];
  }

  /** Die gerade gewaehlte gespeicherte Vertragsvorlage, sonst null. */
  get selectedTemplate(): ContractTemplate | null {
    const source = String(this.form.get('source')!.value || '');
    if (!source.startsWith(SOURCE_TEMPLATE)) return null;
    return this.contractTemplates.find((t) => SOURCE_TEMPLATE + t.id === source) ?? null;
  }

  private get selectedPlan(): ContractTemplatePlan | null {
    const source = String(this.form.get('source')!.value || '');
    if (!source.startsWith(SOURCE_PLAN)) return null;
    return this.templates.find((p) => SOURCE_PLAN + p.id === source) ?? null;
  }

  planSource(plan: ContractTemplatePlan): string {
    return SOURCE_PLAN + plan.id;
  }

  templateSource(template: ContractTemplate): string {
    return SOURCE_TEMPLATE + template.id;
  }

  get featureGroup(): FormGroup {
    return this.form.get('features') as FormGroup;
  }

  featureLabel(key: string): string {
    const i18nKey = 'dialogs.contract.featureNames.' + key;
    const label = this.t.instant(i18nKey);
    return label === i18nKey ? key : label;
  }

  get currentPlanLabel(): string {
    const plan = this.payload?.current_plan;
    if (!plan) return '—';
    return plan.display_name || plan.name;
  }

  get usageEnabled(): boolean {
    return !!this.form.get('usage.enabled')?.value;
  }

  limitGroup(key: ContractLimitKey): FormGroup {
    return this.form.get(['limits', key]) as FormGroup;
  }

  canBeUnlimited(key: ContractLimitKey): boolean {
    return !CONTRACT_LIMITS_WITHOUT_UNLIMITED.includes(key);
  }

  showInputError(path: string | (string | number)[]): boolean {
    const control: AbstractControl | null = this.form.get(path as any);
    return !!control && control.touched && control.invalid;
  }

  // ------------------------------------------------------------------ load

  load(): void {
    if (!this.clientId) return;
    this.loading = true;
    this.loadError = false;
    this.contractService
      .getClientContract(this.clientId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (payload) => this.apply(payload),
        error: () => (this.loadError = true),
      });
  }

  private apply(payload: ContractAdminPayload): void {
    this.payload = payload;
    this.auditView = this.buildAuditView(payload.audit || []);
    this.form.get('source')!.setValue(SOURCE_CURRENT, { emitEvent: false });

    if (payload.contract) {
      const c = payload.contract;
      this.setFeatures(payload.current_plan?.features ?? payload.client_features ?? []);
      this.form.patchValue(
        {
          display_name: c.display_name || payload.current_plan?.display_name || '',
          starts_on: c.starts_on || '',
          ends_on: c.ends_on || '',
          renewal: c.renewal || 'auto',
          notice_days: c.notice_days ?? null,
          billing_cycle: c.billing_cycle || 'month',
          base_fee_eur: (c.base_fee_net_cents ?? 0) / 100,
          payment_terms_days: c.payment_terms_days ?? 14,
          invoice_email: c.invoice_email || '',
          buyer_reference: c.buyer_reference || '',
          po_number: c.po_number || '',
          invoice_mode: c.invoice_mode || 'approve',
          billing_starts_on: c.billing_starts_on || '',
          note: c.note || '',
        },
        { emitEvent: false }
      );
      const usage = (c.usage_terms || []).find((u) => u.metric === 'credits');
      this.form.get('usage')!.patchValue({
        enabled: !!usage,
        included_per_period: usage?.included_per_period ?? 0,
        unit_size: usage?.unit_size ?? 1000,
        unit_price_eur: usage ? usage.unit_price_net_cents / 100 : null,
      });
      this.patchLimits(payload.current_limits || {});
      this.referenceLimits = payload.base_limits || {};
    } else {
      const company = this.companyName || payload.company_name || '';
      this.form.reset(
        {
          source: SOURCE_CURRENT,
          display_name: company
            ? this.t.instant('dialogs.contract.defaultName', { company })
            : '',
          starts_on: toIsoDate(new Date()),
          ends_on: '',
          renewal: 'auto',
          notice_days: null,
          billing_cycle: 'month',
          base_fee_eur: null,
          payment_terms_days: 14,
          invoice_email: '',
          buyer_reference: '',
          po_number: '',
          invoice_mode: 'approve',
          billing_starts_on: '',
          note: '',
        },
        { emitEvent: false }
      );
      this.form.get('usage')!.reset({
        enabled: false,
        included_per_period: 0,
        unit_size: 1000,
        unit_price_eur: null,
      });
      this.applySource(SOURCE_CURRENT);
    }
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.initial = this.collectBody();
  }

  /** Ausgangswerte gewaehlt: fuellt das Formular nur vor. Gespeichert wird
   *  erst mit „Speichern“, danach haengt nichts mehr an der Vorlage. */
  onSourceChange(source: string): void {
    this.applySource(source);
    this.form.markAsDirty();
  }

  private applySource(source: string): void {
    const payload = this.payload;
    if (!payload) return;
    if (source === SOURCE_EMPTY) {
      this.referenceLimits = {};
      this.patchLimits({});
      this.setFeatures([]);
      return;
    }
    if (source === SOURCE_CURRENT) {
      this.referenceLimits = this.isActive ? payload.base_limits || {} : payload.current_limits || {};
      if (!this.isActive) {
        this.patchLimits(payload.current_limits || {});
        this.setFeatures(payload.client_features ?? []);
      }
      return;
    }
    const template = this.selectedTemplate;
    if (template) {
      this.referenceLimits = template.limits || {};
      this.patchLimits(this.referenceLimits);
      this.setFeatures(template.features || []);
      this.patchTerms(template.terms || {});
      return;
    }
    const plan = this.selectedPlan;
    if (plan) {
      this.referenceLimits = plan.limits || {};
      this.patchLimits(this.referenceLimits);
      this.setFeatures(plan.features || []);
      const fee = this.form.get('base_fee_eur')!;
      if (!fee.dirty) {
        const cycle = (this.form.get('billing_cycle')!.value || 'month') as ContractBillingCycle;
        const monthlyNet = plan.monthly_cost_net ?? null;
        fee.setValue(monthlyNet != null ? monthlyNet * CYCLE_MONTHS[cycle] : null, {
          emitEvent: false,
        });
      }
    }
  }

  /** Konditionen einer Vertragsvorlage ins Formular — Kundendaten bleiben. */
  private patchTerms(terms: ContractTemplateTerms): void {
    const patch: Record<string, any> = {};
    if (terms.renewal) patch['renewal'] = terms.renewal;
    if (terms.notice_days !== undefined) patch['notice_days'] = terms.notice_days ?? null;
    if (terms.billing_cycle) patch['billing_cycle'] = terms.billing_cycle;
    if (terms.base_fee_net_cents !== undefined) patch['base_fee_eur'] = (terms.base_fee_net_cents ?? 0) / 100;
    if (terms.payment_terms_days !== undefined) patch['payment_terms_days'] = terms.payment_terms_days;
    if (terms.invoice_mode) patch['invoice_mode'] = terms.invoice_mode;
    const startsOn = this.form.get('starts_on')!.value;
    if (terms.term_months && startsOn) patch['ends_on'] = lastDayAfter(startsOn, terms.term_months);
    this.form.patchValue(patch, { emitEvent: false });
    if (terms.usage_terms !== undefined) {
      const usage = (terms.usage_terms || []).find((u) => u.metric === 'credits');
      this.form.get('usage')!.patchValue({
        enabled: !!usage,
        included_per_period: usage?.included_per_period ?? 0,
        unit_size: usage?.unit_size ?? 1000,
        unit_price_eur: usage ? usage.unit_price_net_cents / 100 : null,
      });
    }
  }

  /** Haekchen fuer genau diese Funktionen setzen; unbekannte bekommen eine Zeile. */
  private setFeatures(features: string[]): void {
    const group = this.featureGroup;
    for (const key of features) {
      if (!this.featureKeys.includes(key)) this.featureKeys = [...this.featureKeys, key];
    }
    for (const key of this.featureKeys) {
      if (!group.contains(key)) group.addControl(key, this.fb.control(false), { emitEvent: false });
      group.get(key)!.setValue(features.includes(key), { emitEvent: false });
    }
  }

  private selectedFeatures(): string[] {
    const value = this.featureGroup.getRawValue() as Record<string, boolean>;
    return this.featureKeys.filter((key) => value[key]).sort();
  }

  private patchLimits(limits: ContractLimits): void {
    for (const key of CONTRACT_LIMIT_KEYS) {
      const raw = limits[key];
      const unlimited = raw === -1 && this.canBeUnlimited(key);
      const value =
        raw == null || raw === -1
          ? null
          : key === 'storage_bytes'
            ? Math.round(raw / BYTES_PER_MB)
            : raw;
      const group = this.limitGroup(key);
      group.get('unlimited')!.setValue(unlimited);
      group.get('value')!.setValue(value, { emitEvent: false });
    }
  }

  // ------------------------------------------------------------------ save

  /** Aus dem Formular den vollstaendigen Body bauen (Betraege in Cent,
   *  Speicher in Bytes, leere Texte als null). */
  private collectBody(): ContractUpsertBody {
    const v = this.form.getRawValue();
    const text = (s: string | null | undefined) => {
      const trimmed = (s ?? '').trim();
      return trimmed ? trimmed : null;
    };
    const num = (n: any): number | null =>
      n === null || n === undefined || n === '' || Number.isNaN(Number(n)) ? null : Number(n);

    const limits: ContractLimits = {};
    for (const key of CONTRACT_LIMIT_KEYS) {
      const group = v.limits[key];
      if (group.unlimited && this.canBeUnlimited(key)) {
        limits[key] = -1;
        continue;
      }
      const value = num(group.value);
      if (value === null) continue;
      limits[key] = key === 'storage_bytes' ? Math.round(value * BYTES_PER_MB) : Math.round(value);
    }

    const usage = v.usage;
    const usageTerms = usage.enabled
      ? [
          {
            metric: 'credits' as const,
            included_per_period: Math.round(num(usage.included_per_period) ?? 0),
            unit_size: Math.max(1, Math.round(num(usage.unit_size) ?? 1000)),
            unit_price_net_cents: Math.round((num(usage.unit_price_eur) ?? 0) * 100),
          },
        ]
      : [];

    return {
      display_name: text(v.display_name),
      starts_on: v.starts_on || null,
      ends_on: v.ends_on || null,
      renewal: v.renewal,
      notice_days: num(v.notice_days),
      billing_cycle: v.billing_cycle,
      base_fee_net_cents: Math.round((num(v.base_fee_eur) ?? 0) * 100),
      payment_terms_days: num(v.payment_terms_days) ?? 14,
      invoice_email: text(v.invoice_email),
      buyer_reference: text(v.buyer_reference),
      po_number: text(v.po_number),
      invoice_mode: v.invoice_mode,
      billing_starts_on: v.billing_starts_on || null,
      note: text(v.note),
      usage_terms: usageTerms,
      features: this.selectedFeatures(),
      limits,
    };
  }

  /** Beim Bearbeiten nur, was sich gegenueber dem geladenen Stand geaendert hat. */
  private changedBody(full: ContractUpsertBody): ContractUpsertBody {
    const before = this.initial || {};
    const out: ContractUpsertBody = {};
    for (const key of Object.keys(full) as (keyof ContractUpsertBody)[]) {
      if (key === 'limits' || key === 'template_plan_id' || key === 'contract_template_id') continue;
      if (JSON.stringify(full[key] ?? null) !== JSON.stringify((before as any)[key] ?? null)) {
        (out as any)[key] = full[key];
      }
    }
    const limits: ContractLimits = {};
    for (const key of CONTRACT_LIMIT_KEYS) {
      const now = full.limits?.[key];
      if (now !== undefined && now !== before.limits?.[key]) {
        limits[key] = now;
      }
    }
    if (Object.keys(limits).length) {
      out.limits = limits;
    }
    return out;
  }

  submit(): void {
    if (!this.payload || this.busy) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const full = this.collectBody();
    let body: ContractUpsertBody;
    if (this.isActive) {
      body = this.changedBody(full);
      if (!Object.keys(body).length) {
        this.toastr.info(
          this.t.instant('dialogs.contract.toasts.noChanges'),
          this.t.instant('dialogs.contract.title')
        );
        return;
      }
    } else {
      const template = this.selectedTemplate;
      body = {
        ...full,
        template_plan_id: this.selectedPlan?.id ?? template?.source_plan_id ?? null,
        contract_template_id: template?.id ?? null,
      };
    }

    const activating = !this.isActive;
    this.saving = true;
    this.contractService
      .saveClientContract(this.clientId, body)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (payload) => {
          this.toastr.success(
            this.t.instant(
              activating ? 'dialogs.contract.toasts.activated' : 'dialogs.contract.toasts.saved'
            ),
            this.t.instant('toasts.success')
          );
          this.apply(payload);
          this.changed.emit(payload);
        },
        // Fehlertext kommt aus dem Error-Interceptor (detail des Backends).
        error: () => undefined,
      });
  }

  // ------------------------------------------------------------------ Vertragsvorlagen

  /** Die Werte des Formulars als Vorlage — ohne Kundendaten. */
  private templateBody(name: string): ContractTemplateBody {
    const full = this.collectBody();
    const v = this.form.getRawValue();
    const source = this.selectedTemplate?.source_plan_id ?? this.selectedPlan?.id ??
      (this.isActive ? this.contract?.base_plan_id ?? null : null);
    return {
      name,
      description: this.selectedTemplate?.description ?? null,
      limits: full.limits || {},
      features: full.features || [],
      source_plan_id: source,
      terms: {
        renewal: full.renewal,
        notice_days: full.notice_days ?? null,
        billing_cycle: full.billing_cycle,
        base_fee_net_cents: full.base_fee_net_cents,
        payment_terms_days: full.payment_terms_days,
        invoice_mode: full.invoice_mode,
        usage_terms: full.usage_terms || [],
        term_months: termMonths(v.starts_on, v.ends_on),
      },
    };
  }

  /** „Als Vorlage speichern“ (neu) oder „Vorlage aktualisieren“ (die gewaehlte,
   *  Name aenderbar). Laufende Vertraege bleiben, wie sie sind. */
  saveAsTemplate(update = false): void {
    if (this.templateBusy) return;
    const existing = update ? this.selectedTemplate : null;
    if (update && !existing) return;
    this.dialogService
      .open(ConfirmDialogComponent, {
        context: {
          title: update ? 'dialogs.contract.templates.updateTitle' : 'dialogs.contract.templates.saveTitle',
          message: this.t.instant(
            update ? 'dialogs.contract.templates.updateMessage' : 'dialogs.contract.templates.saveMessage'
          ),
          confirmLabel: update ? 'dialogs.contract.templates.update' : 'dialogs.contract.templates.save',
          inputLabel: 'dialogs.contract.templates.name',
          inputPlaceholder: 'dialogs.contract.templates.namePlaceholder',
          inputValue: existing?.name ?? '',
          inputMaxLength: 120,
        },
      })
      .onClose.subscribe((result: any) => {
        const name = (result?.value || '').trim();
        if (!result?.confirmed || !name) return;
        const body = this.templateBody(name);
        this.templateBusy = true;
        const request = existing
          ? this.contractService.updateContractTemplate(existing.id, body)
          : this.contractService.createContractTemplate(body);
        request.pipe(finalize(() => (this.templateBusy = false))).subscribe({
          next: (saved) => {
            this.upsertLocalTemplate(saved);
            this.form.get('source')!.setValue(SOURCE_TEMPLATE + saved.id, { emitEvent: false });
            this.referenceLimits = saved.limits || {};
            this.toastr.success(
              this.t.instant(update ? 'dialogs.contract.templates.updated' : 'dialogs.contract.templates.saved', {
                name: saved.name,
              }),
              this.t.instant('toasts.success')
            );
          },
          error: () => undefined,
        });
      });
  }

  deleteTemplate(): void {
    const template = this.selectedTemplate;
    if (!template || this.templateBusy) return;
    this.dialogService
      .open(ConfirmDialogComponent, {
        context: {
          title: 'dialogs.contract.templates.deleteTitle',
          message: this.t.instant('dialogs.contract.templates.deleteMessage', { name: template.name }),
          confirmLabel: 'dialogs.contract.templates.delete',
        },
      })
      .onClose.subscribe((result: any) => {
        if (!(result === true || result?.confirmed)) return;
        this.templateBusy = true;
        this.contractService
          .deleteContractTemplate(template.id)
          .pipe(finalize(() => (this.templateBusy = false)))
          .subscribe({
            next: () => {
              if (this.payload) {
                this.payload = {
                  ...this.payload,
                  contract_templates: this.contractTemplates.filter((t) => t.id !== template.id),
                };
              }
              this.form.get('source')!.setValue(SOURCE_CURRENT, { emitEvent: false });
              this.applySource(SOURCE_CURRENT);
              this.toastr.success(
                this.t.instant('dialogs.contract.templates.deleted', { name: template.name }),
                this.t.instant('toasts.success')
              );
            },
            error: () => undefined,
          });
      });
  }

  private upsertLocalTemplate(saved: ContractTemplate): void {
    if (!this.payload) return;
    const others = this.contractTemplates.filter((t) => t.id !== saved.id);
    this.payload = {
      ...this.payload,
      contract_templates: [...others, saved].sort((a, b) => a.name.localeCompare(b.name)),
    };
  }

  /** Nach einer Rechnungsaktion: nur Rechnungen, Vorschau und Hinweise neu
   *  laden — das Formular (ggf. mit ungespeicherten Aenderungen) bleibt. */
  refreshBilling(): void {
    if (!this.clientId) return;
    this.contractService.getClientContract(this.clientId).subscribe({
      next: (payload) => {
        if (this.payload) {
          this.payload = {
            ...this.payload,
            alphacount_configured: payload.alphacount_configured,
            billing_identity_missing: payload.billing_identity_missing,
            next_invoice: payload.next_invoice,
            invoices: payload.invoices,
          };
        }
      },
      error: () => undefined,
    });
  }

  endContract(): void {
    if (!this.payload?.contract || this.busy) return;
    const fallback = this.t.instant(
      this.payload.has_stripe_subscription
        ? 'dialogs.contract.end.fallbackPrevious'
        : 'dialogs.contract.end.fallbackFree'
    );
    this.dialogService
      .open(ConfirmDialogComponent, {
        context: {
          title: 'dialogs.contract.end.title',
          message: this.t.instant('dialogs.contract.end.message', { plan: fallback }),
          confirmLabel: 'dialogs.contract.buttons.end',
          inputLabel: 'dialogs.contract.end.noteLabel',
          inputPlaceholder: 'dialogs.contract.end.noteLabel',
          inputMaxLength: 2000,
        },
      })
      .onClose.subscribe((result: any) => {
        if (!result?.confirmed) return;
        const note = (result.value || '').trim() || null;
        this.ending = true;
        this.contractService
          .endClientContract(this.clientId, { note })
          .pipe(finalize(() => (this.ending = false)))
          .subscribe({
            next: (payload) => {
              this.toastr.success(
                this.t.instant('dialogs.contract.toasts.ended'),
                this.t.instant('toasts.success')
              );
              this.apply(payload);
              this.changed.emit(payload);
            },
            error: () => undefined,
          });
      });
  }

  // ------------------------------------------------------------------ display

  formatLimit(key: string, value: number | null | undefined): string {
    if (value === null || value === undefined) return '—';
    if (value === -1) return this.t.instant('dialogs.contract.limits.unlimited');
    if (key === 'storage_bytes') {
      return `${this.formatNumber(Math.round(value / BYTES_PER_MB))} MB`;
    }
    return this.formatNumber(value);
  }

  private formatNumber(value: number): string {
    return Number(value).toLocaleString(this.t.currentLang === 'en' ? 'en-GB' : 'de-DE');
  }

  private buildAuditView(entries: ContractAuditEntry[]): AuditView[] {
    return entries.map((entry) => ({
      id: entry.id,
      action: entry.action,
      who:
        entry.changed_by_name ||
        entry.changed_by_email ||
        this.t.instant('dialogs.contract.audit.unknownUser'),
      at: entry.created_at,
      note: entry.note,
      rows: Object.entries(entry.changes || {}).map(([field, pair]) => {
        const [before, after] = Array.isArray(pair) ? pair : [undefined, pair];
        return {
          field,
          label: this.fieldLabel(field),
          before: this.auditValue(field, before),
          after: this.auditValue(field, after),
        };
      }),
    }));
  }

  private fieldLabel(field: string): string {
    if (field.startsWith('limit.')) {
      return this.t.instant('dialogs.contract.limits.' + field.slice('limit.'.length));
    }
    const key = 'dialogs.contract.fields.' + field;
    const label = this.t.instant(key);
    return label === key ? field : label;
  }

  private auditValue(field: string, value: any): string {
    if (value === null || value === undefined || value === '') return '—';
    if (field.startsWith('limit.')) {
      return this.formatLimit(field.slice('limit.'.length), Number(value));
    }
    if (field === 'base_fee_net_cents') {
      return (Number(value) / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
    }
    if (field === 'billing_cycle') {
      return this.t.instant('dialogs.contract.options.cycle.' + value);
    }
    if (field === 'renewal') {
      return this.t.instant('dialogs.contract.options.renewal.' + value);
    }
    if (field === 'invoice_mode') {
      return this.t.instant('dialogs.contract.options.invoiceMode.' + value);
    }
    if (field === 'features' && Array.isArray(value)) {
      return value.length ? value.map((f) => this.featureLabel(String(f))).join(', ') : '—';
    }
    if (Array.isArray(value)) {
      return value.length ? JSON.stringify(value) : '—';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  trackById(_: number, row: { id: string }): string {
    return row.id;
  }
}
